import { ExternalLinkIcon } from '@components/ExternalLinkIcon/ExternalLinkIcon';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import { Tooltip } from '@mui/material';
import { MediaFolderLocation } from '@pages/Library/components/Folders/MediaFolderLocation';
import { CommentCount } from '@pages/Library/components/MediaCard/components/CommentCount';
import { MediaVerbRail } from '@pages/Library/components/MediaCard/components/MediaVerbRail';
import { Media } from '@typings/Media';
import React from 'react';
import css from '../LibraryContentMediaTable.module.scss';
import { HighlightedText } from './HighlightedText';
import { ThumbnailCell } from './ThumbnailCell';
import { TitleCellProps, UploadingMedia } from './types';

export const TitleCell = React.memo<TitleCellProps>(
  ({
    media,
    hasThumbnailError,
    onThumbnailError,
    onThumbnailClick,
    onTitleClick,
    highlightTerms = [],
    matches = [],
  }) => {
    const title = media.isUploading
      ? (media as UploadingMedia).filename
      : media.metadata?.name || 'Unknown';

    return (
      <div className={css.titleCell}>
        <ThumbnailCell
          media={media}
          hasThumbnailError={hasThumbnailError}
          onThumbnailError={onThumbnailError}
          onThumbnailClick={onThumbnailClick}
        />
        <div className={css.textRowContainer}>
          <div className={css.titleRowContainer}>
            <div className={css.titleRow}>
              {!media.isUploading && <MediaFolderLocation media={media} />}
              <span className={css.titleText}>
                <span
                  onClick={(event) => onTitleClick(media, event)}
                  className={css.clickableTitle}
                >
                  <HighlightedText text={title} terms={highlightTerms} />
                </span>
              </span>
              {!media.isUploading && <ExternalLinkIcon origin={media.origin as Media['origin']} />}
              <CommentCount count={media.commentCount} className={css.commentCount} />
              {/* Quick actions at the right edge of the Name column, on the same axis as the
                  thumbnail and the name they act on — the same placement the Libraries table
                  uses. Revealed on row hover by `verb-rail-datagrid-cell`, which also keeps it
                  zero-width at rest so the name owns the whole cell until then.

                  Gated on `uploadStatus`, NOT on `isUploading`. `useMediaData` builds rows for
                  in-flight uploads two ways, and the dangerous one looks finished: once a file
                  has transferred but its backend media is still absent or `'empty'`, it is
                  stamped `isUploading: false, status: 'completed'` so the row doesn't blink out
                  (`useMediaData.ts:66-85`). `useMediaMenuActions` would then read
                  `isCompleted === true` and hand out three ENABLED verbs for media the backend
                  has not indexed — an agent opening a chat about nothing. `uploadStatus` is
                  present on both synthetic shapes and absent on every real row (`:42` returns
                  the file untouched), so it is the honest marker.

                  No spacer handling needed: every row offers the same three verbs, inert ones
                  included, so the glyph columns line up down the table by construction. The only
                  verb that can be absent is the Clipper without `CHAT_URL` — a build-time
                  constant, so it's missing from every row or none. A future gate that varies
                  per media would need `VerbRail`'s `hidden` spacers back. */}
              {!media.uploadStatus && (
                <MediaVerbRail
                  className={css.verbRail}
                  media={media as Media}
                  control="media-table-rail"
                />
              )}
            </div>
          </div>
          {matches.length > 0 && (
            <span className={css.matchReasons}>
              <FilterListRoundedIcon fontSize="inherit" className={css.matchReasonsIcon} />
              Matches{' '}
              {matches.map((match, i) => {
                // Name: show the actual matched search term (not the vague word "name").
                // IDs: show the category label. Reveal a tooltip when there's more to
                // see — all matched name terms, or the matched ID token.
                const isName = match.reason === 'name';
                const extra = match.tokens.length - 1;
                // Name: show the first matched term, plus a "+ N others" hint so it's
                // clear more than one term matched. IDs: show the category label.
                const display =
                  isName && extra > 0
                    ? `${match.tokens[0]} + ${extra} other${extra > 1 ? 's' : ''}`
                    : isName
                      ? match.tokens[0]
                      : match.label;
                const tooltip = isName && match.tokens.length <= 1 ? null : match.tokens.join(', ');
                return (
                  <React.Fragment key={match.reason}>
                    {i > 0 && ', '}
                    {tooltip ? (
                      <Tooltip title={tooltip} placement="top" arrow>
                        <span className={css.matchReasonHint}>{display}</span>
                      </Tooltip>
                    ) : (
                      display
                    )}
                  </React.Fragment>
                );
              })}
            </span>
          )}
        </div>
      </div>
    );
  },
);

TitleCell.displayName = 'TitleCell';
